import { styled } from '@material-ui/core';

import { down } from '../../../../assets/style/breakpointHelpers';

// Party-branded hero shown on step 1 of the application, in place of the plain
// PageHeaderBlock, when the party has published a customization. Colours come in
// as inline styles (computed from the party's brand) so the surface is correct
// on first paint; layout lives here.

export const HeroRoot = styled('div')({
	'position': 'relative',
	'overflow': 'hidden',
	'borderRadius': 4,
	'isolation': 'isolate',
	'color': 'var(--color-on-brand, #ffffff)',
	'display': 'flex',
	'minHeight': 300,
	'animation': 'heroFadeIn 0.5s ease-out both',
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
	[down('md')]: {
		minHeight: 'auto',
	},
});

export const HeroBannerImg = styled('img')({
	position: 'absolute',
	inset: 0,
	width: '100%',
	height: '100%',
	objectFit: 'cover',
	zIndex: -2,
});

export const HeroScrim = styled('div')({
	position: 'absolute',
	inset: 0,
	zIndex: -1,
});

export const HeroInner = styled('div')({
	position: 'relative',
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr) auto',
	gap: 32,
	alignItems: 'center',
	width: '100%',
	boxSizing: 'border-box',
	padding: '40px 44px',
	[down('md')]: {
		gridTemplateColumns: 'minmax(0, 1fr)',
		padding: '28px 20px',
		gap: 20,
	},
});

export const HeroContent = styled('div')({
	'display': 'flex',
	'flexDirection': 'column',
	'gap': 16,
	'minWidth': 0,
	'animation': 'heroFadeUp 0.6s ease-out both',
	'@media (prefers-reduced-motion: reduce)': {
		animation: 'none',
	},
});

export const HeroBrandRow = styled('div')({
	display: 'flex',
	alignItems: 'center',
	gap: 12,
});

export const HeroLogo = styled('img')({
	width: 56,
	height: 56,
	borderRadius: 4,
	objectFit: 'contain',
	background: 'rgba(255, 255, 255, 0.92)',
	padding: 6,
	boxSizing: 'border-box',
	flex: 'none',
});

export const HeroEyebrow = styled('p')({
	margin: 0,
	fontFamily: 'var(--font-family-medium)',
	fontSize: 11,
	letterSpacing: 'var(--tracking-widest)',
	textTransform: 'uppercase',
	opacity: 0.85,
});

export const HeroHeadline = styled('h1')({
	margin: 0,
	fontFamily: 'var(--font-family-display)',
	fontSize: 40,
	lineHeight: 1.1,
	color: 'inherit',
	[down('md')]: {
		fontSize: 30,
	},
	[down('sm')]: {
		fontSize: 26,
	},
});

export const HeroSubheadline = styled('p')({
	margin: 0,
	fontFamily: 'var(--font-family-regular)',
	fontSize: 'var(--text-body-size)',
	lineHeight: 1.55,
	maxWidth: '54ch',
	opacity: 0.92,
});

export const HeroActions = styled('div')({
	display: 'flex',
	alignItems: 'center',
	gap: 16,
	flexWrap: 'wrap',
	marginTop: 8,
});

export const HeroCtaButton = styled('button')({
	'display': 'inline-flex',
	'alignItems': 'center',
	'justifyContent': 'center',
	'minHeight': 48,
	'padding': '0 26px',
	'borderRadius': 4,
	'border': '1px solid rgba(255, 255, 255, 0.24)',
	'cursor': 'pointer',
	'fontFamily': 'var(--font-family-medium)',
	'fontSize': 'var(--text-body-size)',
	'boxShadow': '0 8px 20px rgba(0, 0, 0, 0.22)',
	'transition': 'transform 0.2s ease, box-shadow 0.2s ease',
	'&:hover': {
		transform: 'translateY(-1px)',
		boxShadow: '0 10px 24px rgba(0, 0, 0, 0.28)',
	},
	'&:active': {
		transform: 'translateY(0)',
	},
	'&:focus-visible': {
		outline: '3px solid var(--color-border-focus)',
		outlineOffset: 2,
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

export const HeroSocials = styled('div')({
	display: 'flex',
	alignItems: 'center',
	gap: 10,
	flexWrap: 'wrap',
});

export const HeroSocialLink = styled('a')({
	'display': 'inline-flex',
	'alignItems': 'center',
	'justifyContent': 'center',
	'width': 44,
	'height': 44,
	'borderRadius': 4,
	'color': 'inherit',
	'background': 'rgba(255, 255, 255, 0.16)',
	'transition': 'background 0.2s ease',
	'&:hover': {
		background: 'rgba(255, 255, 255, 0.28)',
	},
	'&:focus-visible': {
		outline: '3px solid var(--color-border-focus)',
		outlineOffset: 2,
	},
	'& svg': {
		width: 20,
		height: 20,
	},
	'@media (prefers-reduced-motion: reduce)': {
		transition: 'none',
	},
});

export const HeroCandidate = styled('div')({
	position: 'relative',
	flex: 'none',
	width: 200,
	[down('md')]: {
		justifySelf: 'start',
		width: 160,
	},
	[down('sm')]: {
		display: 'none',
	},
});

export const HeroCandidatePhoto = styled('img')({
	width: '100%',
	height: 260,
	objectFit: 'cover',
	borderRadius: 4,
	display: 'block',
	boxShadow: '0 12px 28px rgba(0, 0, 0, 0.3)',
	border: '3px solid rgba(255, 255, 255, 0.85)',
	[down('md')]: {
		height: 200,
	},
});
